import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, LayoutGrid, PanelRight, Rows3, X } from 'lucide-react';
import type { SortDir } from '@vidmakr/shared';
import type { SortState, ViewMode } from '@/lib/browse';
import { Button, cx } from './ui';
import { Splitter, usePanelSize } from './Splitter';

/** Shared pieces of the project browser and the asset library: view toggle, sortable table headers, the inspector panel. */

export function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  const btn = (mode: ViewMode, icon: ReactNode, label: string) => (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={value === mode}
      data-view-mode={mode}
      onClick={() => onChange(mode)}
      className={cx('flex h-8 w-8 items-center justify-center rounded-md', value === mode ? 'bg-panel-3 text-fg' : 'text-muted hover:text-fg')}
    >
      {icon}
    </button>
  );
  return (
    <div className="flex items-center gap-0.5 rounded-lg border border-line bg-panel p-0.5" role="group" aria-label="View">
      {btn('table', <Rows3 size={15} />, 'Table view')}
      {btn('grid', <LayoutGrid size={15} />, 'Grid view')}
    </div>
  );
}

export function InspectorToggle({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <Button variant={open ? 'secondary' : 'ghost'} size="icon" aria-label={open ? 'Hide details' : 'Show details'} title="Details" aria-pressed={open} onClick={onClick} data-inspector-toggle>
      <PanelRight size={16} />
    </Button>
  );
}

/** A column header that sorts the table; text columns sort A→Z first, the others largest/newest first. */
export function SortTh<K extends string>({
  k,
  sort,
  onSort,
  first = 'desc',
  className,
  align = 'left',
  children,
}: {
  k: K;
  sort: SortState<K>;
  onSort: (k: K, first: SortDir) => void;
  first?: SortDir;
  className?: string;
  align?: 'left' | 'right';
  children: ReactNode;
}) {
  const active = sort.key === k;
  return (
    <th className={cx('whitespace-nowrap px-3 py-2 align-middle font-medium', align === 'right' ? 'text-right' : 'text-left', className)} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
      <button
        type="button"
        className={cx('inline-flex h-5 items-center gap-1 rounded align-middle leading-none hover:text-fg', active ? 'text-fg' : 'text-muted', align === 'right' && 'flex-row-reverse')}
        onClick={() => onSort(k, first)}
        data-sort={k}
      >
        {children}
        {active ? sort.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} /> : <span className="w-3" />}
      </button>
    </th>
  );
}

/** Table classes shared by both pages. Narrow screens hide the less important columns (hidden sm:table-cell…); the wrapper scrolls if it still doesn't fit. */
export const tableCls = {
  wrap: 'overflow-x-auto rounded-xl border border-line bg-panel',
  table: 'w-full border-collapse text-sm',
  head: 'border-b border-line text-xs',
  row: 'group cursor-pointer select-none border-b border-line/60 last:border-b-0',
  cell: 'whitespace-nowrap px-3 py-2 text-muted',
};

/** Row colors for selection and drop highlighting. */
export function rowTone(selected: boolean, dropping: boolean) {
  return dropping ? 'bg-accent/15 outline outline-2 -outline-offset-2 outline-accent' : selected ? 'bg-accent/10 outline outline-1 -outline-offset-1 outline-accent/60' : 'hover:bg-panel-2';
}

/**
 * The details panel on the right. Next to the content on wide screens; a
 * sheet over the page on narrow ones. `data-inspector` keeps clicks in it
 * from starting a box selection or clearing the selection.
 */
const DETAILS_SIZE = { key: 'vm:detailsW', initial: 288, min: 240, max: 560 };

export function InspectorPanel({ open, wide, onClose, children }: { open: boolean; wide: boolean; onClose: () => void; children: ReactNode }) {
  const [width, setWidth] = usePanelSize(DETAILS_SIZE);
  if (!open) return null;
  const head = (
    <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-faint">Details</span>
      <Button variant="ghost" size="icon-sm" aria-label="Close details" onClick={onClose}>
        <X size={14} />
      </Button>
    </div>
  );
  if (wide) {
    return (
      <div data-inspector className="sticky top-0 shrink-0 self-start" style={{ width }}>
        <div className="absolute inset-y-0 -left-2.5 flex">
          <Splitter size={width} onSize={setWidth} limits={DETAILS_SIZE} edge="left" label="Resize the details panel" />
        </div>
        <aside className="max-h-[calc(100vh-4rem)] overflow-y-auto rounded-xl border border-line bg-panel">
          {head}
          <div className="p-4">{children}</div>
        </aside>
      </div>
    );
  }
  return (
    <>
      <div data-inspector className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <aside data-inspector className="fixed inset-y-0 right-0 z-50 w-80 max-w-[90vw] overflow-y-auto border-l border-line bg-panel pb-[env(safe-area-inset-bottom)] shadow-2xl">
        {head}
        <div className="p-4">{children}</div>
      </aside>
    </>
  );
}

/** A heading block at the top of the inspector. */
export function InfoTitle({ icon, title, subtitle }: { icon?: ReactNode; title: ReactNode; subtitle?: ReactNode }) {
  return (
    <div className="mb-3 flex items-start gap-2.5">
      {icon ? <div className="mt-0.5 shrink-0">{icon}</div> : null}
      <div className="min-w-0">
        <div className="break-words text-sm font-semibold" data-inspector-title>
          {title}
        </div>
        {subtitle ? <div className="mt-0.5 text-xs text-faint">{subtitle}</div> : null}
      </div>
    </div>
  );
}

export function InfoSection({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="mb-3 border-t border-line pt-3 first:border-t-0 first:pt-0">
      {title ? <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-faint">{title}</div> : null}
      <dl className="space-y-1">{children}</dl>
    </div>
  );
}

/** One label/value line; empty values show as "—". */
export function InfoRow({ label, children }: { label: string; children?: ReactNode }) {
  const empty = children === undefined || children === null || children === '' || children === false;
  return (
    <div className="flex justify-between gap-3 text-xs">
      <dt className="shrink-0 text-faint">{label}</dt>
      <dd className="min-w-0 break-words text-right text-fg">{empty ? '—' : children}</dd>
    </div>
  );
}
